// Flow B stage 2: what the rules cannot settle. Today: cancellations (a seat on a non-refundable
// flight) and bounced invitations (fix the address, or leave it). Each row's Work it opens the record
// alongside the decision (Task B4). Exceptions and extra-badge requests arrive with the January customer work.
import React, { useState } from "react";
import { api } from "../../lib/api.js";
import { usePoll } from "../../lib/usePoll.js";
import { PageHead, Notice, fmtWhen } from "./ui.jsx";
import { fmtWaiting } from "../../lib/when.js";
import { useConfirm } from "../ConfirmDialog.jsx";
import WorkItPanel from "./WorkItPanel.jsx";

export default function HumanPage({ isAdmin, reload }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);   // the item in the Work it panel; it closes when a refresh drops the item
  const [panelError, setPanelError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [history, setHistory] = useState(null);
  const [dialog, ask] = useConfirm();
  const say = (text, tone = "ok") => setMsg({ text, tone });
  const load = async () => { try { setItems((await api("/backoffice/queue")).items); } catch (err) { say(err.message, "bad"); } };
  // A decision that fails shows in the panel (the page's notice sits behind it) and on the page once the panel is gone.
  const fail = (err) => { setPanelError(err.message); say(err.message, "bad"); load(); };
  const done = (r, text) => { setOpenId(id => id === r.id ? null : id); say(text); load(); reload?.(); };
  const open = items.find(i => i.id === openId) ?? null;
  usePoll(load, 15000);

  async function decide(r, decision) {
    const declining = decision === "declined";
    const answer = await ask({
      title: `${declining ? "Decline" : "Approve"} the cancellation for ${r.who}?`,
      body: declining ? "Nothing changes: they stay on the code." : "Their seat and badge come off the code. The code stays with whoever is left.",
      note: declining ? "Tell them why (optional)" : "Note for the record (optional)",
      // The registrant leaving while others aren't booked: the team chooses whether those people book themselves.
      check: !declining && r.isRegistrant && r.othersUnbooked > 0 ? "Email the others their codes so they can book themselves" : undefined,
      confirmLabel: declining ? "Decline" : "Approve",
    });
    if (!answer) return;
    const { note, checked } = answer;
    try {
      const res = await api(`/backoffice/cancellations/${r.id}/decide`, { method: "POST", body: { decision, note, ...(checked && { sendCodes: true }) } });
      done(r, `${r.who}: ${decision}.${res.codesSent ? ` Codes sent to ${res.codesSent} ${res.codesSent === 1 ? "person" : "people"}.` : ""}`);
    }
    catch (err) { fail(err); }
  }
  async function readdress(r, email) {
    if (!email) return;
    try { const res = await api(`/backoffice/invitations/${r.id}/readdress`, { method: "POST", body: { email } }); done(r, `${r.who}: invitation re-sent to ${res.email}.`); }
    catch (err) { fail(err); }
  }
  async function leave(r) {
    if (!(await ask({ title: `Leave ${r.who} without an invitation?`, body: "The bounced one is withdrawn; you can invite them again later.", confirmLabel: "Leave it" }))) return;
    try { await api(`/backoffice/invitations/${r.id}/revoke`, { method: "POST", body: {} }); done(r, `${r.who}: left without an invitation.`); } catch (err) { fail(err); }
  }
  async function showHistory() { try { setHistory((await api("/backoffice/cancellations?status=approved")).requests.concat((await api("/backoffice/cancellations?status=declined")).requests)); } catch (err) { say(err.message, "bad"); } }

  const shown = items.filter(i => tab === "all" || i.type === tab);
  const n = (t) => items.filter(i => i.type === t).length;
  return (
    <>
      <PageHead title="Needs a human" sub="Nothing here was decided by the app. A seat on a flight is a cancellation decision; a bounced email needs an address only a person can supply. Hotel names change without you until the cutoff." />
      <div className="bo-filter-row">
        <div className="lbl">Type</div>
        {[["all", "Everything", items.length], ["cancellation", "Can't travel", n("cancellation")], ["bounce", "Bounced", n("bounce")]].map(([v, l, c]) => (
          <button key={v} className={`bo-pill ${tab === v ? "on" : ""}`} onClick={() => setTab(v)}>{l} <span className="n">{c}</span></button>))}
        <button className="bo-link" style={{ marginLeft: "auto" }} onClick={showHistory}>Decided earlier</button>
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {dialog}
      <div className="bo-table queue">
        <div className="bo-th"><div>Type</div><div>Who and what happened</div><div>Waiting</div><div /></div>
        {shown.map(r => (
          <div className={`bo-tr ${open?.id === r.id ? "picked" : ""}`} key={r.id}>
            <div>{r.type === "cancellation" ? <span className="bo-state bad">Can't travel</span> : <span className="bo-state mid">Bounced</span>}</div>
            <div className="name"><b>{r.who}</b> {r.type === "cancellation"
              ? <span className="muted small"><span className="mono">{r.code}</span> · {r.registrant}'s registration · {r.travelMethod === "B" ? "flying" : "driving"}</span>
              : <span className="muted small mono">{r.email}</span>}
              <div className="what">{r.type === "cancellation" ? (r.detail ? `“${r.detail}”` : "No reason given") : `Could not be delivered${r.detail ? `: ${r.detail}` : ""}`}</div></div>
            <div className="waiting" title={fmtWhen(r.at)}>{fmtWaiting(r.at)}</div>
            <div className="actions"><button className="bo-btn primary" onClick={() => { setOpenId(r.id); setPanelError(null); }}>Work it</button></div>
          </div>
        ))}
        {!shown.length && <div className="bo-empty">Nothing here. Change the filter, or carry on.</div>}
      </div>
      {open && <WorkItPanel key={open.id} item={open} isAdmin={isAdmin} error={panelError} onClose={() => setOpenId(null)}
        onDecide={(d) => decide(open, d)} onReaddress={(email) => readdress(open, email)} onLeave={() => leave(open)} />}
      {history && (
        <>
          <h2 className="bo-h2">Decided earlier</h2>
          <div className="bo-table human">
            {history.map(h => <div className="bo-tr" key={h.id}><div><span className={`bo-state ${h.status === "approved" ? "ok" : ""}`}>{h.status === "approved" ? "Approved" : "Declined"}</span></div><div className="name"><b>{h.travellerName}</b><div className="muted small mono">{h.codes}</div></div><div className="muted">{h.reason || "—"}{h.note && <div className="small">Note: {h.note}</div>}</div><div className="muted">{fmtWhen(h.decidedAt)}</div><div /></div>)}
            {!history.length && <div className="bo-empty">No decisions yet.</div>}
          </div>
        </>
      )}
    </>
  );
}
